import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceAreasMap = () => {
  const [selectedArea, setSelectedArea] = useState(null);

  const serviceAreas = [
    {
      id: 1,
      name: "Sandton",
      coordinates: { lat: -26.1076, lng: 28.0567 },
      coverage: "Premium Service",
      responseTime: "30 minutes",
      services: ["Luxury Cleaning", "Premium Flooring", "Concierge"],
      projects: 150,
      rating: 4.9
    },
    {
      id: 2,
      name: "Midrand",
      coordinates: { lat: -25.9885, lng: 28.1289 },
      coverage: "Full Service",
      responseTime: "35 minutes",
      services: ["All Cleaning", "All Flooring", "Emergency"],
      projects: 124,
      rating: 4.8
    },
    {
      id: 3,
      name: "Centurion",
      coordinates: { lat: -25.8601, lng: 28.1888 },
      coverage: "Full Service",
      responseTime: "40 minutes",
      services: ["All Cleaning", "All Flooring", "Emergency"],
      projects: 98,
      rating: 4.7
    },
    {
      id: 4,
      name: "Johannesburg",
      coordinates: { lat: -26.2041, lng: 28.0473 },
      coverage: "Full Service",
      responseTime: "45 minutes",
      services: ["All Cleaning", "All Flooring", "Emergency"],
      projects: 189,
      rating: 4.9
    },
    {
      id: 5,
      name: "Germiston",
      coordinates: { lat: -26.2309, lng: 28.1772 },
      coverage: "Standard Service",
      responseTime: "50 minutes",
      services: ["Basic Cleaning", "Standard Flooring"],
      projects: 67,
      rating: 4.6
    },
    {
      id: 6,
      name: "Boksburg",
      coordinates: { lat: -26.2147, lng: 28.2629 },
      coverage: "Standard Service",
      responseTime: "55 minutes",
      services: ["Basic Cleaning", "Standard Flooring"],
      projects: 45,
      rating: 4.5
    }
  ];

  const handleAreaClick = (area) => {
    setSelectedArea(selectedArea?.id === area?.id ? null : area);
  };

  const getCoverageColor = (coverage) => {
    switch (coverage) {
      case 'Premium Service':
        return 'text-warning';
      case 'Full Service':
        return 'text-success';
      default:
        return 'text-primary';
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Service Areas We Cover
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            We proudly serve multiple areas across Gauteng with fast response times and comprehensive 
            cleaning and flooring solutions tailored to each community's needs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Interactive Map */}
          <div className="order-2 lg:order-1">
            <div className="bg-card rounded-xl p-6 shadow-elevation-2">
              <h3 className="text-xl font-semibold text-text-primary mb-6 flex items-center">
                <Icon name="MapPin" size={20} className="mr-2" />
                Coverage Map
              </h3>
              
              {/* Google Maps Embed */}
              <div className="relative h-96 rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Clean360 Service Areas"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=-26.1076,28.0567&z=10&output=embed"
                  className="border-0"
                />
                
                {/* Overlay with service area markers */}
                <div className="absolute inset-0 pointer-events-none">
                  {serviceAreas?.map((area) => (
                    <div
                      key={area?.id}
                      className={`absolute w-3 h-3 rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer transition-all duration-200 ${
                        selectedArea?.id === area?.id 
                          ? 'bg-primary scale-150 shadow-elevation-2' 
                          : 'bg-primary/70 hover:bg-primary hover:scale-125'
                      }`}
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`
                      }}
                      onClick={() => handleAreaClick(area)}
                    />
                  ))}
                </div>
              </div>

              {/* Map Legend */}
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-warning rounded-full mr-2" />
                  <span className="text-text-secondary">Premium Service</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-success rounded-full mr-2" />
                  <span className="text-text-secondary">Full Service</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-primary rounded-full mr-2" />
                  <span className="text-text-secondary">Standard Service</span>
                </div>
              </div>
            </div>
          </div>

          {/* Service Areas List */}
          <div className="order-1 lg:order-2">
            <div className="space-y-4">
              {serviceAreas?.map((area) => (
                <div
                  key={area?.id}
                  className={`bg-card rounded-lg p-6 border transition-all duration-200 cursor-pointer ${
                    selectedArea?.id === area?.id
                      ? 'border-primary shadow-elevation-2'
                      : 'border-border hover:border-primary/50 hover:shadow-elevation-1'
                  }`}
                  onClick={() => handleAreaClick(area)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-text-primary mb-1">
                        {area?.name}
                      </h4>
                      <div className={`text-sm font-medium ${getCoverageColor(area?.coverage)}`}>
                        {area?.coverage}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-sm text-text-secondary mb-1">
                        <Icon name="Star" size={14} className="text-warning mr-1" />
                        {area?.rating}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {area?.projects} projects
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Clock" size={14} className="mr-2" />
                      {area?.responseTime}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="MapPin" size={14} className="mr-2" />
                      Available 24/7
                    </div>
                  </div>

                  {/* Services Available */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {area?.services?.map((service, index) => (
                      <span
                        key={index}
                        className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  {/* Expanded Details */}
                  {selectedArea?.id === area?.id && (
                    <div className="border-t border-border pt-4 mt-4 space-y-3">
                      <div className="text-sm text-text-secondary">
                        <strong>Coverage Details:</strong> Complete cleaning and flooring services 
                        with professional equipment and certified technicians.
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          iconName="Phone"
                          iconPosition="left"
                        >
                          Call Now
                        </Button>
                        <Link to="/smart-contact">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Calendar"
                            iconPosition="left"
                          >
                            Book Service
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Service Area CTA */}
            <div className="mt-8 bg-primary/5 rounded-lg p-6 border border-primary/20">
              <h4 className="text-lg font-semibold text-text-primary mb-2">
                Don't See Your Area?
              </h4>
              <p className="text-text-secondary mb-4">
                We're constantly expanding our service areas across Gauteng. Contact us to check 
                availability in your location or to request service expansion.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/smart-contact">
                  <Button
                    variant="default"
                    iconName="MessageCircle"
                    iconPosition="left"
                  >
                    Request Service Area
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  iconName="Phone"
                  iconPosition="left"
                >
                  Call (011) 123-4567
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-16 pt-16 border-t border-border">
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">6</div>
            <div className="text-text-secondary">Service Areas</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">38min</div>
            <div className="text-text-secondary">Avg Response Time</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">673</div>
            <div className="text-text-secondary">Total Projects</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">24/7</div>
            <div className="text-text-secondary">Emergency Service</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreasMap;